import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Edit } from 'lucide-react';
import { authedJson } from '../../utils/adminApi';
import { uploadImageToSiteMedia } from '../../utils/adminMedia';

export default function StreamsCreatorsEditor() {
  const [creators, setCreators] = useState([]);
  const [streams, setStreams] = useState([]);
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  useEffect(() => {
    const load = async () => {
      const [c, s] = await Promise.all([
        authedJson('/api/admin/creators').catch(() => []),
        authedJson('/api/admin/streams').catch(() => [])
      ]);
      setCreators(Array.isArray(c) ? c : []);
      setStreams(Array.isArray(s) ? s : []);
    };
    load();
  }, []);

  const [creatorForm, setCreatorForm] = useState({ id: '', name: '', displayName: '', image: '' });
  const [editingCreatorIdx, setEditingCreatorIdx] = useState(-1);
  const [streamForm, setStreamForm] = useState({
    id: '',
    title: '',
    host: '',
    time: '',
    game: '',
    viewers: '',
    thumbnail: '',
    status: 'upcoming',
    streamUrl: ''
  });
  const [editingStreamIdx, setEditingStreamIdx] = useState(-1);

  const addOrUpdateCreator = async (e) => {
    e.preventDefault();
    setFeedback({ error: '', success: '' });
    try {
      if (editingCreatorIdx >= 0) {
        const current = creators[editingCreatorIdx];
        const updatedItem = await authedJson('/api/admin/creators', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: current.id,
            name: creatorForm.name,
            display_name: creatorForm.displayName,
            image_url: creatorForm.image
          })
        });
        const updated = [...creators];
        updated[editingCreatorIdx] = updatedItem;
        setCreators(updated);
        setEditingCreatorIdx(-1);
        setFeedback({ error: '', success: 'Hall of Fame entry updated.' });
      } else {
        const created = await authedJson('/api/admin/creators', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: creatorForm.name,
            display_name: creatorForm.displayName,
            image_url: creatorForm.image
          })
        });
        setCreators([created, ...creators]);
        setFeedback({ error: '', success: 'Hall of Fame entry added.' });
      }
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
    setCreatorForm({ id: '', name: '', displayName: '', image: '' });
  };

  const editCreator = (idx) => {
    const c = creators[idx];
    setEditingCreatorIdx(idx);
    setCreatorForm({ id: c.id, name: c.name, displayName: c.display_name, image: c.image_url });
  };

  const deleteCreator = async (idx) => {
    if (!window.confirm('Delete this creator?')) return;
    setFeedback({ error: '', success: '' });
    try {
      const id = creators[idx].id;
      await authedJson('/api/admin/creators', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      setCreators(creators.filter((_, i) => i !== idx));
      setFeedback({ error: '', success: 'Hall of Fame entry deleted.' });
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
  };

  const addOrUpdateStream = async (e) => {
    e.preventDefault();
    setFeedback({ error: '', success: '' });
    try {
      if (editingStreamIdx >= 0) {
        const current = streams[editingStreamIdx];
        const updatedItem = await authedJson('/api/admin/streams', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: current.id,
            title: streamForm.title,
            host: streamForm.host,
            time: streamForm.time,
            game: streamForm.game,
            viewers: streamForm.viewers,
            thumbnail_url: streamForm.thumbnail,
            status: streamForm.status,
            stream_url: streamForm.streamUrl
          })
        });
        const updated = [...streams];
        updated[editingStreamIdx] = updatedItem;
        setStreams(updated);
        setEditingStreamIdx(-1);
        setFeedback({ error: '', success: 'Stream updated.' });
      } else {
        const created = await authedJson('/api/admin/streams', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: streamForm.title,
            host: streamForm.host,
            time: streamForm.time,
            game: streamForm.game,
            viewers: streamForm.viewers,
            thumbnail_url: streamForm.thumbnail,
            status: streamForm.status,
            stream_url: streamForm.streamUrl
          })
        });
        setStreams([created, ...streams]);
        setFeedback({ error: '', success: 'Stream added.' });
      }
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
    setStreamForm({ id: '', title: '', host: '', time: '', game: '', viewers: '', thumbnail: '', status: 'upcoming', streamUrl: '' });
  };

  const editStream = (idx) => {
    const s = streams[idx];
    setEditingStreamIdx(idx);
    setStreamForm({
      id: s.id,
      title: s.title,
      host: s.host,
      time: s.time,
      game: s.game,
      viewers: s.viewers,
      thumbnail: s.thumbnail_url,
      status: s.status || 'upcoming',
      streamUrl: s.stream_url || ''
    });
  };

  const deleteStream = async (idx) => {
    if (!window.confirm('Delete this stream?')) return;
    setFeedback({ error: '', success: '' });
    try {
      const id = streams[idx].id;
      await authedJson('/api/admin/streams', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      setStreams(streams.filter((_, i) => i !== idx));
      setFeedback({ error: '', success: 'Stream deleted.' });
    } catch (error) {
      setFeedback({ error: error.message, success: '' });
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }} className="bg-[#0A0E11] rounded-xl p-8 border border-[#A1FF4F]/10">
        <h2 className="text-2xl font-bold mb-4">Hall of Fame Editor</h2>
        {feedback.error && <p className="mb-3 text-red-300 text-sm">{feedback.error}</p>}
        {feedback.success && <p className="mb-3 text-[#A1FF4F] text-sm">{feedback.success}</p>}
        <form onSubmit={addOrUpdateCreator} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <input value={creatorForm.name} onChange={(e) => setCreatorForm({ ...creatorForm, name: e.target.value })} placeholder="username" className="p-3 bg-[#0F1419] rounded" />
          <input value={creatorForm.displayName} onChange={(e) => setCreatorForm({ ...creatorForm, displayName: e.target.value })} placeholder="display name" className="p-3 bg-[#0F1419] rounded" />
          <input value={creatorForm.image} onChange={(e) => setCreatorForm({ ...creatorForm, image: e.target.value })} placeholder="image url (optional)" className="p-3 bg-[#0F1419] rounded" />
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await uploadImageToSiteMedia(file, 'creators');
                setCreatorForm({ ...creatorForm, image: url });
              } catch (error) {
                setFeedback({ error: error.message, success: '' });
              }
            }}
            className="p-3 bg-[#0F1419] rounded text-white/70"
          />
          <div className="flex items-center gap-3">
            <button type="submit" className="px-4 py-2 bg-[#A1FF4F]/20 text-[#A1FF4F] rounded">
              {editingCreatorIdx >= 0 ? 'Update' : 'Add'}
            </button>
            {editingCreatorIdx >= 0 && <button type="button" onClick={() => { setEditingCreatorIdx(-1); setCreatorForm({ id: '', name: '', displayName: '', image: '' }); }} className="px-3 py-2 bg-white/5 rounded">Cancel</button>}
          </div>
        </form>

        <div className="space-y-3">
          {creators.map((c, idx) => (
            <div key={c.id} className="flex items-center justify-between p-3 bg-white/5 rounded">
              <div className="flex items-center gap-3">
                <img src={c.image_url || c.image} alt={c.name} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <div className="font-semibold">{c.display_name || c.displayName}</div>
                  <div className="text-sm text-white/60">{c.name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => editCreator(idx)} className="p-2 bg-white/5 rounded"><Edit className="w-4 h-4" /></button>
                <button onClick={() => deleteCreator(idx)} className="p-2 bg-red-600/20 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        {creators.length === 0 && <p className="text-white/60 mt-4">No Hall of Fame entries yet.</p>}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }} className="bg-[#0A0E11] rounded-xl p-8 border border-[#A1FF4F]/10">
        <h2 className="text-2xl font-bold mb-4">Streams Editor</h2>
        <form onSubmit={addOrUpdateStream} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <input value={streamForm.title} onChange={(e) => setStreamForm({ ...streamForm, title: e.target.value })} placeholder="title" className="p-3 bg-[#0F1419] rounded" />
          <input value={streamForm.host} onChange={(e) => setStreamForm({ ...streamForm, host: e.target.value })} placeholder="host" className="p-3 bg-[#0F1419] rounded" />
          <input value={streamForm.time} onChange={(e) => setStreamForm({ ...streamForm, time: e.target.value })} placeholder="time" className="p-3 bg-[#0F1419] rounded" />
          <input value={streamForm.streamUrl} onChange={(e) => setStreamForm({ ...streamForm, streamUrl: e.target.value })} placeholder="stream link (YouTube/Twitch/etc)" className="p-3 bg-[#0F1419] rounded" />
          <select value={streamForm.status} onChange={(e) => setStreamForm({ ...streamForm, status: e.target.value })} className="p-3 bg-[#0F1419] rounded text-white/80">
            <option value="upcoming">upcoming</option>
            <option value="past">past</option>
          </select>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const url = await uploadImageToSiteMedia(file, 'streams');
                setStreamForm({ ...streamForm, thumbnail: url });
              } catch (error) {
                setFeedback({ error: error.message, success: '' });
              }
            }}
            className="p-3 bg-[#0F1419] rounded text-white/70"
          />
          <input value={streamForm.game} onChange={(e) => setStreamForm({ ...streamForm, game: e.target.value })} placeholder="game" className="p-3 bg-[#0F1419] rounded" />
          <input value={streamForm.viewers} onChange={(e) => setStreamForm({ ...streamForm, viewers: e.target.value })} placeholder="viewers" className="p-3 bg-[#0F1419] rounded" />
          <div className="col-span-4 flex items-center gap-3">
            <button type="submit" className="px-4 py-2 bg-[#A1FF4F]/20 text-[#A1FF4F] rounded">
              {editingStreamIdx >= 0 ? 'Update' : 'Add'}
            </button>
            {editingStreamIdx >= 0 && <button type="button" onClick={() => { setEditingStreamIdx(-1); setStreamForm({ id: '', title: '', host: '', time: '', game: '', viewers: '', thumbnail: '', status: 'upcoming', streamUrl: '' }); }} className="px-3 py-2 bg-white/5 rounded">Cancel</button>}
          </div>
        </form>

        <div className="space-y-3">
          {streams.map((s, idx) => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-white/5 rounded">
              <div className="flex items-center gap-3">
                {s.thumbnail_url ? (
                  <img src={s.thumbnail_url} alt={s.title} className="w-16 h-10 object-cover rounded" />
                ) : (
                  <div className="w-16 h-10 rounded bg-white/10" />
                )}
                <div>
                  <div className="font-semibold">{s.title}</div>
                  <div className="text-sm text-white/60">{s.host} • {s.time} • {s.status || 'upcoming'}</div>
                  {s.stream_url ? <a href={s.stream_url} target="_blank" rel="noreferrer" className="text-xs text-[#A1FF4F]">open stream link</a> : null}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => editStream(idx)} className="p-2 bg-white/5 rounded"><Edit className="w-4 h-4" /></button>
                <button onClick={() => deleteStream(idx)} className="p-2 bg-red-600/20 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
        {streams.length === 0 && <p className="text-white/60 mt-4">No streams yet.</p>}
      </motion.div>
    </>
  );
}
